import { Machines, MachineSession } from '../datamodels';
import { doc, Timestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import {
  startSession,
  endSession,
  addMachineSession,
  deleteMachineSession,
  cancelSession,
} from './TrainingSessionService';
import { addSet, removeSet } from '../utils/TrainingSessionUtils';
import { createLocalId } from '../repositories/LocalRepositories';

type MachineSessionSetter = React.Dispatch<React.SetStateAction<MachineSession[]>>;

export async function startWorkout(userId: string): Promise<{ sessionId: string; startedAt: number } | null> {
  if (!userId) return null;

  const startedAt = Date.now();
  const sessionId = createLocalId();

  const persistedId = await startSession(userId, () => {}, () => {}, () => {}, sessionId);
  if (!persistedId) return null;
  return { sessionId, startedAt };
}

export async function finishWorkout(
  sessionId: string,
  machineSessions: MachineSession[],
  setMachineSessions: MachineSessionSetter
): Promise<void> {
  await endSession(sessionId, () => {}, machineSessions, setMachineSessions);
}

export async function cancelWorkout(
  sessionId: string,
  onSuccess: () => void,
  onError?: (err: any) => void
): Promise<void> {
  await cancelSession(sessionId, onSuccess, onError);
}

export async function addMachineToWorkout(
  userId: string,
  sessionId: string,
  machine: Machines,
  exerciseName: string,
  reps: number,
  weight: number,
  machineSessions: MachineSession[],
  setMachineSessions: MachineSessionSetter
): Promise<void> {
  const existingSessionIndex = machineSessions.findIndex(
    session =>
      session.machine_ref.id === machine.id &&
      session.exercise_name === exerciseName
  );

  let optimisticMachineSessionId: string | undefined;

  if (existingSessionIndex !== -1) {
    const existingSession = machineSessions[existingSessionIndex];
    const optimisticSessions = [...machineSessions];
    optimisticSessions[existingSessionIndex] = {
      ...existingSession,
      sets: [
        ...existingSession.sets,
        {
          set_number: existingSession.sets.length + 1,
          reps,
          weight,
        },
      ],
    };
    setMachineSessions(optimisticSessions);
  } else {
    optimisticMachineSessionId = createLocalId();
    const optimisticSession: MachineSession = {
      id: optimisticMachineSessionId,
      training_session_id: sessionId,
      machine_ref: doc(firestore, 'machines', machine.id),
      exercise_name: exerciseName,
      date_used: Timestamp.now(),
      sets: [{ set_number: 1, reps, weight }],
      user_id: userId,
    };
    setMachineSessions(prev => [...prev, optimisticSession]);
  }

  await addMachineSession(
    userId,
    sessionId,
    machine,
    exerciseName,
    reps,
    weight,
    machineSessions,
    setMachineSessions,
    optimisticMachineSessionId
  );
}

export async function appendSetToWorkout(
  machineSessions: MachineSession[],
  sessionIndex: number,
  reps: number,
  weight: number
): Promise<MachineSession[]> {
  const machineSessionId = machineSessions[sessionIndex].id;
  return addSet(machineSessions, sessionIndex, machineSessionId, reps, weight);
}

export async function removeSetFromWorkout(
  machineSessions: MachineSession[],
  sessionIndex: number,
  setIndex: number
): Promise<MachineSession[]> {
  const machineSessionId = machineSessions[sessionIndex].id;
  return removeSet(machineSessions, sessionIndex, setIndex, machineSessionId);
}

export async function removeMachineFromWorkout(
  machineSessionId: string,
  sessionId: string,
  machineSessions: MachineSession[],
  setMachineSessions: MachineSessionSetter
): Promise<void> {
  await deleteMachineSession(
    machineSessionId,
    sessionId,
    machineSessions,
    setMachineSessions
  );
}
